import { useHeaderConnection } from "@client/HeaderConnectionContext";
import { useImpersonateContext } from "@client/ImpersonateContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sfc/ui";
import type { ReactNode } from "react";

const MAX_VISIBLE = 5;

// Stack Tooltip + Popover so desktop hover and touch tap both reveal the
// content. Base UI `render` lets both triggers compose onto the same button.
function AvatarWithReveal({
  ariaLabel,
  className,
  children,
  content,
}: {
  ariaLabel: string;
  className: string;
  children: ReactNode;
  content: ReactNode;
}) {
  return (
    <Popover>
      <Tooltip>
        <PopoverTrigger
          render={
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label={ariaLabel}
                  className={className}
                >
                  {children}
                </button>
              }
            />
          }
        />
        <TooltipContent>{content}</TooltipContent>
      </Tooltip>
      <PopoverContent className="w-auto px-3 py-1.5 text-sm">
        {content}
      </PopoverContent>
    </Popover>
  );
}

export function PresenceAvatars() {
  const headerConnection = useHeaderConnection();
  const { value } = useImpersonateContext();

  if (!headerConnection || !value) return null;

  const { connectedUserIds } = headerConnection;
  const { userProfiles, round } = value;
  const currentUserId = value.mode.viewAsUserId ?? value.underlyingUserId;

  const otherUserIds = connectedUserIds.filter(
    (id) => id !== currentUserId && round.members[id]?.status === "active",
  );
  if (otherUserIds.length === 0) return null;

  const visibleIds = otherUserIds.slice(0, MAX_VISIBLE);
  const overflowCount = otherUserIds.length - MAX_VISIBLE;

  function getInitials(userId: string): string {
    const profile = userProfiles[userId];
    const name = profile?.name ?? profile?.email;
    if (!name) return "??";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  function getDisplayName(userId: string): string {
    const profile = userProfiles[userId];
    return profile?.name ?? profile?.email ?? userId;
  }

  function getMemberColor(userId: string): string {
    const member = round.members[userId];
    const track = member?.track;
    if (track === "NO_TRACK" || !track) return "bg-surface-elevated";
    // Use a simple hash of the track name for consistent color
    const colors = [
      "bg-blue-500/20",
      "bg-green-500/20",
      "bg-amber-500/20",
      "bg-purple-500/20",
      "bg-rose-500/20",
      "bg-teal-500/20",
    ];
    let hash = 0;
    for (let i = 0; i < track.length; i++) {
      hash = (hash * 31 + track.charCodeAt(i)) | 0;
    }
    return colors[Math.abs(hash) % colors.length]!;
  }

  const baseAvatarClass =
    "h-7 w-7 rounded-full border-2 border-surface-base flex items-center justify-center text-[10px] font-semibold";

  return (
    <div className="flex items-center -space-x-2">
      {visibleIds.map((userId) => (
        <AvatarWithReveal
          key={userId}
          ariaLabel={`Viewer: ${getDisplayName(userId)}`}
          className={`${baseAvatarClass} ${getMemberColor(userId)} text-foreground`}
          content={getDisplayName(userId)}
        >
          {getInitials(userId)}
        </AvatarWithReveal>
      ))}
      {overflowCount > 0 && (
        <AvatarWithReveal
          ariaLabel={`${overflowCount} more viewers`}
          className={`${baseAvatarClass} bg-surface-elevated text-muted-foreground`}
          content={otherUserIds
            .slice(MAX_VISIBLE)
            .map((id) => getDisplayName(id))
            .join(", ")}
        >
          +{overflowCount}
        </AvatarWithReveal>
      )}
    </div>
  );
}
